import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import {
  AdminTable, AdminModal, ConfirmDialog, FormField,
  LoadingState, ErrorState, ApiModeBanner, inputCls,
} from "../components/AdminUI";
import { useAdminT } from "../../../hooks/useAdminT";
import { useToast } from "../../../components/base/Toast";
import { useAdminData } from "../../../store/adminDataStore";
import type { BookingNote, NoteType } from "../../../store/types";

const NOTE_TYPES: NoteType[] = ["internal", "customer_visible", "photographer_visible"];

const NOTE_TYPE_STYLES: Record<NoteType, { badge: string; row: string; icon: string }> = {
  internal: {
    badge: "bg-amber-50 text-amber-600",
    row: "bg-amber-50/20",
    icon: "ri-lock-line",
  },
  customer_visible: {
    badge: "bg-emerald-50 text-emerald-600",
    row: "",
    icon: "ri-user-line",
  },
  photographer_visible: {
    badge: "bg-[#C8B8A6]/20 text-[#C8B8A6]",
    row: "",
    icon: "ri-camera-line",
  },
};

const NOW = () => new Date().toISOString();

function CreateModal({
  form, onChange, onClose, onSave, saving,
  orders,
}: {
  form: BookingNote; onChange: (v: BookingNote) => void;
  onClose: () => void; onSave: () => void; saving: boolean;
  orders: Array<{ id: string; label: string }>;
}) {
  const t = useAdminT();
  const set = (field: keyof BookingNote, value: unknown) => onChange({ ...form, [field]: value });
  return (
    <AdminModal
      title={t("tabifotoAdmin.booking_notes.add_note")}
      onClose={onClose} onSave={onSave} saving={saving}
    >
      <FormField label={t("tabifotoAdmin.booking_notes.order_id")} required>
        <select className={inputCls} value={form.order_id} onChange={(e) => set("order_id", e.target.value)}>
          <option value="">— {t("tabifotoAdmin.booking_notes.order_id")} —</option>
          {orders.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
      </FormField>
      <div className="grid grid-cols-2 gap-4">
        <FormField label={t("tabifotoAdmin.booking_notes.note_type")} required>
          <select className={inputCls} value={form.note_type} onChange={(e) => set("note_type", e.target.value as NoteType)}>
            {NOTE_TYPES.map((n) => (
              <option key={n} value={n}>{t(`tabifotoAdmin.booking_notes.note_type_${n}`)}</option>
            ))}
          </select>
        </FormField>
        <FormField label={t("tabifotoAdmin.booking_notes.created_by")}>
          <input className={inputCls} value={form.created_by}
            onChange={(e) => set("created_by", e.target.value)} placeholder="admin@tabifoto.com" />
        </FormField>
      </div>
      <FormField label={t("tabifotoAdmin.booking_notes.content")} required>
        <textarea
          className={`${inputCls} h-32 resize-none`}
          value={form.content}
          onChange={(e) => set("content", e.target.value)}
          placeholder={t("tabifotoAdmin.booking_notes.content_placeholder")}
          maxLength={500}
        />
        <p className="text-right text-xs text-[#2C2C2C]/30 mt-1">{form.content.length} / 500</p>
      </FormField>
    </AdminModal>
  );
}

export default function AdminBookingNotesPage() {
  const t = useAdminT();
  const { state, status, isApiMode, upsertBookingNote, removeBookingNote, refetchBookingNotes } = useAdminData();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preFilterOrder = searchParams.get("order") ?? "all";

  const [showCreate, setShowCreate] = useState(false);
  const [newNote, setNewNote] = useState<BookingNote>({
    id: `bn-${Date.now()}`, order_id: preFilterOrder !== "all" ? preFilterOrder : "",
    note_type: "internal", content: "", created_by: "", created_at: NOW(),
  });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filterOrder, setFilterOrder] = useState<string>(preFilterOrder);
  const [filterType, setFilterType] = useState<NoteType | "all">("all");

  const orders = state.orders.map((o) => ({ id: o.id, label: `${o.order_number} — ${o.customer_name}` }));

  const filtered = [...state.bookingNotes]
    .filter((n) => {
      if (filterOrder !== "all" && n.order_id !== filterOrder) return false;
      if (filterType !== "all" && n.note_type !== filterType) return false;
      return true;
    })
    .sort((a, b) => b.created_at.localeCompare(a.created_at));

  const handleSave = async () => {
    if (!newNote.order_id || !newNote.content.trim()) { toast.error(t("tabifotoAdmin.common.save_error")); return; }
    setSaving(true);
    try {
      await upsertBookingNote({ ...newNote, id: `bn-${Date.now()}` });
      toast.success(t("tabifotoAdmin.common.save_success"));
      setShowCreate(false);
      setNewNote({ id: `bn-${Date.now()}`, order_id: "", note_type: "internal", content: "", created_by: "", created_at: NOW() });
    } catch { toast.error(t("tabifotoAdmin.common.save_error")); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await removeBookingNote(deleteId);
      toast.success(t("tabifotoAdmin.common.delete_success"));
    } catch { toast.error(t("tabifotoAdmin.common.delete_error")); }
    finally { setDeleteId(null); }
  };

  const TYPE_FILTER_TABS = [
    { key: "all" as const, label: t("tabifotoAdmin.booking_notes.filter_all") },
    ...NOTE_TYPES.map((n) => ({ key: n, label: t(`tabifotoAdmin.booking_notes.note_type_${n}`) })),
  ];

  const headers = [
    t("tabifotoAdmin.booking_notes.col_order"),
    t("tabifotoAdmin.booking_notes.col_note_type"),
    t("tabifotoAdmin.booking_notes.col_content"),
    t("tabifotoAdmin.booking_notes.col_created_by"),
    t("tabifotoAdmin.booking_notes.col_created_at"),
    t("tabifotoAdmin.booking_notes.col_actions"),
  ];

  return (
    <AdminLayout
      title={t("tabifotoAdmin.booking_notes.title")}
      subtitle={t("tabifotoAdmin.booking_notes.subtitle")}
      action={
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#2C2C2C] text-white text-sm rounded-md hover:bg-[#2C2C2C]/80 transition-colors cursor-pointer whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-base" /></div>
          {t("tabifotoAdmin.booking_notes.add_note")}
        </button>
      }
    >
      <ApiModeBanner isApiMode={isApiMode} />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex gap-1 bg-[#F7F6F3] p-1 rounded-lg">
          <button onClick={() => setFilterOrder("all")}
            className={`px-3 py-1.5 rounded-md text-xs font-light transition-colors cursor-pointer whitespace-nowrap ${filterOrder === "all" ? "bg-white text-[#2C2C2C]" : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"}`}>
            {t("tabifotoAdmin.booking_notes.filter_all")}
          </button>
          {orders.map((o) => (
            <button key={o.id} onClick={() => setFilterOrder(o.id)}
              className={`px-3 py-1.5 rounded-md text-xs font-light transition-colors cursor-pointer whitespace-nowrap ${filterOrder === o.id ? "bg-white text-[#2C2C2C]" : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"}`}>
              {o.label.split(" — ")[0]}
            </button>
          ))}
        </div>
        <div className="flex gap-1 bg-[#F7F6F3] p-1 rounded-lg">
          {TYPE_FILTER_TABS.map((tab) => (
            <button key={tab.key} onClick={() => setFilterType(tab.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-light transition-colors cursor-pointer whitespace-nowrap ${filterType === tab.key ? "bg-white text-[#2C2C2C]" : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"}`}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {status.bookingNotes.loading ? <LoadingState /> :
        status.bookingNotes.error ? <ErrorState message={status.bookingNotes.error} onRetry={refetchBookingNotes} /> : (
          <AdminTable headers={headers} empty={filtered.length === 0} emptyMessage={t("tabifotoAdmin.booking_notes.no_notes")}>
            {filtered.map((note) => {
              const order = state.orders.find((o) => o.id === note.order_id);
              const style = NOTE_TYPE_STYLES[note.note_type];
              return (
                <tr key={note.id} className={`border-b border-[#D9D9D9]/40 hover:bg-[#F7F6F3]/60 transition-colors ${style.row}`}>
                  <td className="px-5 py-3">
                    <button onClick={() => navigate(`/admin/orders?order=${note.order_id}`)}
                      className="text-xs font-mono text-[#C8B8A6] hover:text-[#2C2C2C] cursor-pointer whitespace-nowrap">
                      {order ? order.order_number : note.order_id}
                    </button>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-light ${style.badge}`}>
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className={`${style.icon} text-xs`} />
                      </div>
                      {t(`tabifotoAdmin.booking_notes.note_type_${note.note_type}`)}
                    </span>
                  </td>
                  <td className="px-5 py-3 max-w-sm">
                    <p className="text-sm text-[#2C2C2C]/80 font-light leading-relaxed line-clamp-2">{note.content}</p>
                  </td>
                  <td className="px-5 py-3 text-xs text-[#2C2C2C]/40 font-light">{note.created_by || "—"}</td>
                  <td className="px-5 py-3 text-xs text-[#2C2C2C]/40">{note.created_at.slice(0, 10)}</td>
                  <td className="px-5 py-3">
                    <button onClick={() => setDeleteId(note.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer">
                      <i className="ri-delete-bin-line text-base" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </AdminTable>
        )}

      {showCreate && (
        <CreateModal form={newNote} onChange={setNewNote} onClose={() => setShowCreate(false)}
          onSave={handleSave} saving={saving} orders={orders} />
      )}
      {deleteId && (
        <ConfirmDialog title={t("tabifotoAdmin.booking_notes.delete_confirm")}
          message={t("tabifotoAdmin.common.cannot_undone")} danger
          onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
      )}
    </AdminLayout>
  );
}
